import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { httpClient } from "@/shared/api/http-client";
import { getActiveTenantId } from "@/shared/tenant/active-tenant";
import type { TaskDetail, TaskListResponse, TaskScope, TaskSummary } from "./types";

export interface TaskListParams {
  scope?: TaskScope;
  status?: "PENDING" | "COMPLETED" | "CANCELLED";
  overdue?: boolean;
  page?: number;
  pageSize?: number;
}

function toQuery(params: TaskListParams): string {
  const q = new URLSearchParams();
  if (params.scope) q.set("scope", params.scope);
  if (params.status) q.set("status", params.status);
  if (params.overdue) q.set("overdue", "true");
  if (params.page) q.set("page", String(params.page));
  if (params.pageSize) q.set("pageSize", String(params.pageSize));
  const s = q.toString();
  return s ? `?${s}` : "";
}

/** GET /api/tasks — the current user's tasks (a REP is always scoped to their own server-side). */
export function useTasks(params: TaskListParams = {}) {
  return useQuery({
    queryKey: ["tasks", getActiveTenantId(), params] as const,
    queryFn: () => httpClient<TaskListResponse>(`/api/tasks${toQuery(params)}`),
  });
}

/** GET /api/tasks/:id — task detail + the lead needed to work it. */
export function useTaskDetail(id: string | undefined) {
  return useQuery({
    queryKey: ["tasks", getActiveTenantId(), "detail", id] as const,
    queryFn: () => httpClient<TaskDetail>(`/api/tasks/${id}`),
    enabled: !!id,
  });
}

/**
 * GET /api/tasks/summary — counts for the sidebar badge (and dashboard tile). Polled so the
 * badge stays roughly live without websockets; also refetched on window focus by default.
 */
export function useTaskSummary() {
  return useQuery({
    queryKey: ["tasks", getActiveTenantId(), "summary"] as const,
    queryFn: () => httpClient<TaskSummary>(`/api/tasks/summary`),
    refetchInterval: 60_000,
  });
}

/** POST /api/tasks/:id/complete — mark done; the server advances the sequence if it was a blocker. */
export function useCompleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => httpClient<{ id: string; status: string }>(`/api/tasks/${id}/complete`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tasks"] });
    },
  });
}
